<template>
  <atoms-card
    class="mx-auto"
    v-bind="card"
  >
    <p
      class="font-bold mb-2"
      :class="{
        'mt-3 text-black text-xl': theme === 'default',
        'text-xl md:text-2xl': theme === 'v2'
      }"
    >
      {{ title }}
    </p>

    <!-- eslint-disable vue/no-v-html -->
    <div
      v-if="ctaType !== 'stickyState'"
      :class="[
        {
          'text-sm text-gray-125 mb-2': theme === 'default',
          'text-sm md:text-base': theme === 'v2'
        },
        '[&>p]:my-4'
      ]"
      v-html="subtitle"
    >
    </div>

    <hr v-if="theme === 'default' || ctaType === 'stickyState'" />

    <!-- eslint-disable vue/no-v-html -->
    <div
      v-if="ctaType === 'stickyState'"
      class="mt-4 text-sm text-gray-125"
      v-html="subtitle"
    >
    </div>

    <div
      class="mx-auto grid"
      :class="{
        'grid-cols-2 gap-x-4 gap-y-4 md:grid-cols-4 md:!gap-[0.3125rem]': ctaType === 'State' && theme === 'default',
        'grid-cols-1 gap-x-4 gap-y-2 md:grid-cols-2': ctaType !== 'State' && theme === 'default',
        'my-6': theme === 'default',
        'mt-4': theme === 'v2'
      }"
    >
      <template v-if="theme === 'default'">
        <atoms-cta
          v-for="(option, index) in options"
          :id="ctaId"
          :key="index"
          :to="option.link"
          :rounded="
            ctaType === 'stickyState'
              ? 'standard'
              : 'full'
          "
          :size="
            ctaType === 'stickyState'
              ? 'large'
              : 'small'
          "
          class="flex items-center font-normal hover:bg-dark hover:border-dark"
          :class="{
            '!px-0': ctaType === 'State'
          }"
        >
          <span class="text-white text-lg font-bold mx-auto">
            {{ option.label }}
          </span>
        </atoms-cta>
      </template>

      <MoleculesSelection
        v-else-if="theme === 'v2' && !_isEmpty(options)"
        v-model="range"
        ticker="circle"
        type="radio"
        name="range"
        :options="options"
        :cols="2"
        :mobile-cols="1"
      />

      <atoms-button
        v-else-if="ctaType === 'Default'"
        class="mx-auto"
        size="large"
        :to="redirectUrl"
        target="_blank"
      >
        {{ buttonLabel }}
      </atoms-button>
    </div>
  </atoms-card>
</template>

<script setup>
import _isEmpty from 'underscore/cjs/isEmpty.js'

defineOptions({
  name: 'MoleculesInlineCardCta'
})

const props = defineProps({
  ctaId: {
    type: String,
    default: 'inline-card-cta'
  },

  ctaClass: {
    type: String,
    default: ''
  },

  vertical: {
    type: String,
    default: 'Home Loans'
  },

  customComponent: {
    type: Object,
    default: () => ({})
  },

  title: {
    type: String,
    default: 'Select your state and compare now!'
  },

  subtitle: {
    type: String,
    default: 'Discover the best options for you'
  },

  redirectUrl: {
    type: String,
    default: '/home-loans/quote/form/step1/'
  },

  ctaType: {
    type: String,
    default: 'State',
    validator: value => ['Default', 'State', 'stickyState', 'AgeRange'].includes(value)
  },

  theme: {
    type: String,
    default: 'default',
    validator: value => ['default', 'v2'].includes(value)
  },

  buttonLabel: {
    type: String,
    default: 'COMPARE NOW'
  }
})

const range = ref('')

const card = computed(() => {
  const theme = props.theme
  const ctaType = props.ctaType

  return getKey(theme, {
    default: {
      shadow: 'bordered',
      rounded: 'md',
      size: ctaType === 'stickyState'
        ? 'md'
        : 'standard',
      class: 'text-center md:max-w-1.5xl'
    },
    v2: {
      shadow: 'none',
      rounded: 'none',
      theme: 'none',
      size: 'lg',
      class: 'bg-blue-5 text-dark !pb-8 !-mx-4 md:!mx-0 md:!rounded-2.5xl md:!border-1 md:!border-gray-400'
    }
  })
})

const options = computed(() => {
  const ctaType = props.ctaType.replace(/\s/g, '')
  const theme = props.theme

  const list = getKey(ctaType, {
    default: [],
    AgeRange: [
      {
        label: '< 35 years',
        link: props.redirectUrl
      },
      {
        label: '35 - 49 years',
        link: props.redirectUrl
      },
      {
        label: '50 - 64 years',
        link: props.redirectUrl
      },
      {
        label: '> 65 years',
        link: props.redirectUrl
      }
    ],
    State: [
      {
        label: 'NSW',
        link: props.redirectUrl
      },
      {
        label: 'VIC',
        link: props.redirectUrl
      },
      {
        label: 'QLD',
        link: props.redirectUrl
      },
      {
        label: 'SA',
        link: props.redirectUrl
      },
      {
        label: 'WA',
        link: props.redirectUrl
      },
      {
        label: 'ACT',
        link: props.redirectUrl
      },
      {
        label: 'TAS',
        link: props.redirectUrl
      },
      {
        label: 'NT',
        link: props.redirectUrl
      }
    ],
    stickyState: [
      {
        label: 'NSW',
        link: props.redirectUrl
      },
      {
        label: 'VIC',
        link: props.redirectUrl
      },
      {
        label: 'QLD',
        link: props.redirectUrl
      },
      {
        label: 'SA',
        link: props.redirectUrl
      },
      {
        label: 'WA',
        link: props.redirectUrl
      },
      {
        label: 'ACT',
        link: props.redirectUrl
      },
      {
        label: 'TAS',
        link: props.redirectUrl
      },
      {
        label: 'NT',
        link: props.redirectUrl
      }
    ]
  })

  if (theme === 'v2') {
    return (list && list.map(e => {
      return {
        ...e,
        value: e.label
      }
    })) || []
  }

  return list
})

const emit = defineEmits(['redirection'])
watch(range, value => {
  if (value) {
    const link = options.find(e => e.value === range.value)?.link || ''
    // window.location = link
    emit('redirection', { path: link })
  }
})
</script>
